import React, { useState, useEffect } from 'react';
import { 
  Building2, 
  Home, 
  TrendingUp,
  DollarSign,
  PieChart,
  Users,
  Briefcase
} from 'lucide-react';
import { dashboardApi } from '../../lib/api';
import { formatCurrency, formatPeriodo, cn } from '../../lib/utils';
import { LoadingState, ErrorState } from '../../components/common/EmptyState';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';

const KPICard = ({ icon: Icon, label, value, subValue, color = 'slate' }) => {
  const colorClasses = {
    slate: 'bg-slate-100 text-slate-600',
    green: 'bg-green-100 text-green-600',
    amber: 'bg-amber-100 text-amber-600',
    blue: 'bg-blue-100 text-blue-600',
    purple: 'bg-purple-100 text-purple-600',
  };
  
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-center gap-3">
        <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0", colorClasses[color])}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-2xl font-bold text-slate-900 leading-tight">{value}</p>
          <p className="text-sm text-slate-500">{label}</p>
          {subValue && <p className="text-xs text-slate-400 mt-0.5">{subValue}</p>}
        </div>
      </div>
    </div>
  );
};

const FeeChart = ({ data, edificioNombre }) => {
  const formatXAxis = (value) => {
    const [year, month] = value.split('-');
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return months[parseInt(month) - 1];
  };

  const formatTooltipValue = (value) => formatCurrency(value, 'ARS');

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-slate-900">Evolución de Fees</h3>
          <p className="text-xs text-slate-500">Últimos 12 meses</p>
        </div>
      </div>
      
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 5, right: 5, left: 5, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis 
              dataKey="periodo" 
              tickFormatter={formatXAxis}
              tick={{ fontSize: 10, fill: '#64748b' }}
              axisLine={{ stroke: '#e2e8f0' }}
            />
            <YAxis 
              tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              tick={{ fontSize: 10, fill: '#64748b' }}
              axisLine={{ stroke: '#e2e8f0' }}
              width={50}
            />
            <Tooltip 
              formatter={(value) => [formatTooltipValue(value), 'Fee']}
              labelFormatter={(label) => formatPeriodo(label)}
              contentStyle={{ 
                backgroundColor: 'white', 
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '12px'
              }}
            />
            <Bar dataKey="fee" fill="#1B4D3E" radius={[3, 3, 0, 0]} maxBarSize={35} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const IngresosChart = ({ data }) => {
  const formatXAxis = (value) => {
    const [year, month] = value.split('-');
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return months[parseInt(month) - 1];
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-slate-900">Ingresos vs Ganancia</h3>
          <p className="text-xs text-slate-500">Últimos 12 meses</p>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-blue-500"></div>
            <span className="text-slate-500">Ingresos</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-green-500"></div>
            <span className="text-slate-500">Ganancia</span>
          </div>
        </div>
      </div>
      
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 5, left: 5, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis 
              dataKey="periodo" 
              tickFormatter={formatXAxis}
              tick={{ fontSize: 10, fill: '#64748b' }}
              axisLine={{ stroke: '#e2e8f0' }}
            />
            <YAxis 
              tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              tick={{ fontSize: 10, fill: '#64748b' }}
              axisLine={{ stroke: '#e2e8f0' }}
              width={50}
            />
            <Tooltip 
              formatter={(value) => [formatCurrency(value, 'ARS')]}
              labelFormatter={(label) => formatPeriodo(label)}
              contentStyle={{ 
                backgroundColor: 'white', 
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '12px'
              }}
            />
            <Line type="monotone" dataKey="ingreso" stroke="#3b82f6" strokeWidth={2} dot={false} name="Ingresos" />
            <Line type="monotone" dataKey="ganancia" stroke="#22c55e" strokeWidth={2} dot={false} name="Ganancia" />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export const AdminDashboard = () => {
  const [kpis, setKpis] = useState(null);
  const [feesData, setFeesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEdificio, setSelectedEdificio] = useState('all');

  useEffect(() => {
    fetchData(selectedEdificio === 'all' ? null : selectedEdificio);
  }, [selectedEdificio]);

  const fetchData = async (edificioId) => {
    try {
      setLoading(true);
      const [kpisRes, feesRes] = await Promise.all([
        dashboardApi.kpis(edificioId),
        dashboardApi.feesEvolucion(edificioId),
      ]);
      setKpis(kpisRes.data);
      setFeesData(feesRes.data);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Error al cargar los datos del dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Get chart data for the selected edificio
  const getChartData = () => {
    if (!feesData?.edificios?.length) return [];
    
    // If a specific edificio is selected, use its data
    if (selectedEdificio !== 'all' && feesData.edificios.length === 1) {
      return feesData.edificios[0].data;
    }
    
    // Otherwise aggregate all edificios data
    const aggregated = {};
    feesData.edificios.forEach(edif => {
      edif.data.forEach(d => {
        if (!aggregated[d.periodo]) {
          aggregated[d.periodo] = { periodo: d.periodo, fee: 0, ingreso: 0, egreso: 0, ganancia: 0 };
        }
        aggregated[d.periodo].fee += d.fee;
        aggregated[d.periodo].ingreso += d.ingreso;
        aggregated[d.periodo].egreso += d.egreso;
        aggregated[d.periodo].ganancia += d.ganancia;
      });
    });
    
    return Object.values(aggregated).sort((a, b) => a.periodo.localeCompare(b.periodo));
  };

  if (loading && !kpis) return <LoadingState message="Cargando dashboard..." />;
  if (error) return <ErrorState message={error} onRetry={() => fetchData(selectedEdificio === 'all' ? null : selectedEdificio)} />;

  const chartData = getChartData();
  const totalFee12m = feesData?.edificios?.reduce((sum, e) => sum + e.total_fee_12m, 0) || 0;

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto animate-fadeIn">
      {/* Header with Edificio Selector */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-900">
            Dashboard {kpis?.edificio_nombre ? `- ${kpis.edificio_nombre}` : ''}
          </h1>
          <p className="text-sm text-slate-500">
            Rendimiento en tiempo real del edificio
          </p>
        </div>
        
        <div className="w-full md:w-64">
          <Select value={selectedEdificio} onValueChange={setSelectedEdificio}>
            <SelectTrigger data-testid="edificio-selector">
              <Building2 className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Todos los edificios" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los edificios</SelectItem>
              {kpis?.edificios_list?.map((edif) => (
                <SelectItem key={edif.edificio_id} value={edif.edificio_id}>
                  {edif.nombre}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPIs Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KPICard
          icon={Home}
          label="Unidades Totales"
          value={kpis?.total_unidades || 0}
          color="blue"
        />
        <KPICard
          icon={Briefcase}
          label="En Administración"
          value={kpis?.unidades_administradas || 0}
          subValue={`${kpis?.unidades_admin_full || 0} full · ${kpis?.unidades_admin_simple || 0} simple`}
          color="purple"
        />
        <KPICard
          icon={Users}
          label="Alquiladas"
          value={kpis?.unidades_alquiladas || 0}
          subValue={`${kpis?.unidades_vacantes || 0} vacantes`}
          color="green"
        />
        <KPICard
          icon={PieChart}
          label="Ocupación"
          value={`${kpis?.ocupacion_pct || 0}%`}
          subValue="sobre administradas"
          color="amber"
        />
      </div>

      {/* Monthly Summary */}
      {kpis?.ultimo_periodo && (
        <div className="bg-gradient-to-r from-[#1B4D3E] to-[#2d6b56] rounded-lg p-4 mb-6 text-white">
          <div className="flex items-center gap-2 mb-3">
            <DollarSign className="w-4 h-4" />
            <h2 className="text-base font-semibold">
              Resumen {formatPeriodo(kpis.ultimo_periodo)}
              {kpis.edificio_nombre && <span className="font-normal opacity-80"> · {kpis.edificio_nombre}</span>}
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-white/70 text-xs mb-0.5">Ingresos</p>
              <p className="text-lg font-bold font-mono">{formatCurrency(kpis.total_ingresos_mes, 'ARS')}</p>
            </div>
            <div>
              <p className="text-white/70 text-xs mb-0.5">Egresos</p>
              <p className="text-lg font-bold font-mono">{formatCurrency(kpis.total_egresos_mes, 'ARS')}</p>
            </div>
            <div>
              <p className="text-white/70 text-xs mb-0.5">Fee Admin</p>
              <p className="text-lg font-bold font-mono">{formatCurrency(kpis.total_fees_mes, 'ARS')}</p>
            </div>
            <div>
              <p className="text-white/70 text-xs mb-0.5">Ganancia Neta</p>
              <p className="text-lg font-bold font-mono">{formatCurrency(kpis.total_ganancia_mes, 'ARS')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <FeeChart 
          data={chartData} 
          edificioNombre={kpis?.edificio_nombre}
        />
        <IngresosChart data={chartData} />
      </div>

      {/* Total Fee 12m */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-center">
        <p className="text-sm text-slate-500 mb-1">Total Fees Administración (12 meses)</p>
        <p className="text-2xl font-bold font-mono text-[#1B4D3E]">
          {formatCurrency(totalFee12m, 'ARS')}
        </p>
      </div>
    </div>
  );
};

export default AdminDashboard;
